import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBreadcrumb } from '../../contexts/BreadcrumbContext';
import { listTenants, createTenant, updateTenant, Tenant } from '../../services/tenantService';
import PageHeader from '../shell/PageHeader';
import { DataTable, FilterBar, Notice } from '../patterns';
import type { DataTableColumn } from '../patterns';
import TenantEditModal from './TenantEditModal';
import { formatDate } from '../../dateFormat';

const SystemTenantsPage: React.FC = () => {
  const { t } = useTranslation();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<Tenant | null>(null);
  const [showModal, setShowModal] = useState(false);

  useBreadcrumb([
    { label: t('nav.system.module'), to: '/system' },
    { label: t('system.tenants.title') },
  ]);

  const fetchTenants = async () => {
    try {
      setLoading(true);
      const data = await listTenants();
      setTenants(data);
      setError(null);
    } catch {
      setError(t('system.tenants.errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTenants(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    if (!search.trim()) return tenants;
    const q = search.toLowerCase();
    return tenants.filter(tn =>
      tn.name.toLowerCase().includes(q) || tn.tenantId.toLowerCase().includes(q)
    );
  }, [tenants, search]);

  const columns: DataTableColumn<Tenant>[] = [
    { key: 'name',         label: t('system.tenants.columns.name'),     sortable: true },
    { key: 'tenantId',     label: t('system.tenants.columns.tenantId'), sortable: true },
    { key: 'farmTimezone', label: t('system.tenants.columns.timezone') },
    {
      key: 'createdAt',
      label: t('system.tenants.columns.created'),
      render: row => row.createdAt ? formatDate(row.createdAt) : '—',
      sortable: true,
    },
  ];

  const handleNew = () => { setEditing(null); setShowModal(true); };
  const handleRowClick = (row: Tenant) => { setEditing(row); setShowModal(true); };

  const handleSave = async (input: { tenantId: string; name: string; farmTimezone: string }) => {
    if (editing) {
      await updateTenant(editing.tenantId, { name: input.name, farmTimezone: input.farmTimezone });
    } else {
      await createTenant(input);
    }
    await fetchTenants();
  };

  return (
    <>
      <PageHeader
        title={t('system.tenants.title')}
        meta={t('system.tenants.metaCount', { count: tenants.length })}
        actions={
          <button className="pat-btn pat-btn--primary" onClick={handleNew}>
            {t('system.tenants.newButton')}
          </button>
        }
      />
      {error && <Notice tone="danger">{error}</Notice>}
      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('system.tenants.searchPlaceholder')}
      />
      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={r => r._id}
        onRowClick={handleRowClick}
        emptyState={loading ? '…' : t('system.tenants.empty')}
      />
      <TenantEditModal
        show={showModal}
        tenant={editing}
        onHide={() => setShowModal(false)}
        onSave={handleSave}
      />
    </>
  );
};

export default SystemTenantsPage;
